import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { GroupedBundleLeaf } from "@/pages/bundles/types/grouped-bundle";

type BundlePriceLineProps = {
  bundle: GroupedBundleLeaf;
  // "current · since 12 Jan 2026" or a history range "01 Jun 2025 – 12 Jan 2026"
  label: string;
  // current price is emphasized; history lines are muted
  emphasis?: boolean;
  onClick: (bundle: GroupedBundleLeaf) => void;
};

export default function BundlePriceLine({
  bundle,
  label,
  emphasis = false,
  onClick,
}: BundlePriceLineProps) {
  const code = bundle.region_rel?.code;

  return (
    <div
      role="button"
      onClick={() => onClick(bundle)}
      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-t border-gray-100 hover:bg-gray-50 ${
        emphasis ? "bg-white" : "bg-gray-50/40"
      }`}
    >
      <span
        className="text-base leading-none"
        title={bundle.region_rel?.display_name ?? "No region"}
      >
        {codeToFlag(code)}
      </span>
      <span className="w-8 text-xs font-medium text-gray-400 uppercase">
        {code ?? "—"}
      </span>

      <span
        className={
          emphasis
            ? "font-semibold text-sm text-gray-900 w-28"
            : "text-sm text-gray-500 w-28"
        }
      >
        {bundle.currency} {bundle.amount.toLocaleString()}
      </span>

      {emphasis && (
        <Badge variant="active" className="text-[10px]">
          current
        </Badge>
      )}

      <span className="text-xs text-gray-500">{label}</span>

      <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
    </div>
  );
}
